import isEmpty from 'lodash.isempty'

import { removeEmptyValuesInObject } from '@core/helpers'
import { createTTL, isPastDate } from '@core/helpers/date'
import { BUNDLE_ID, PLATFORM } from '@http/apiConstants'
import APILogger from '@http/apiLogger'
import { algoliaRefinementsFetch } from '@http/endpoints'

import {
  FACET_CARRIER,
  FACET_FILTER,
  FACET_GRAPH,
  orderFacets,
  useAlgolia,
} from '../utils/algolia'

export const RANGE_BAR = 20
const SEARCH_CACHE_DURATION_IN_MINUTES = 5

const doesSearchExist = (state, searchId) => {
  return (
    searchId in state.searchResult &&
    !isPastDate(state.searchResult[searchId].ttl)
  )
}

const doesGraphExist = (state, graphId) => {
  return graphId in state.graphResults
}

export default {
  namespaced: true,
  state() {
    return {
      ongoing: {
        contextId: null,
        searchId: null,
        graphId: null,
      },
      contexts: {},
      searchResult: {},
      graphResults: {},
    }
  },
  getters: {
    doesContextExist: (state) => {
      return state.ongoing.contextId in state.contexts
    },

    doesGraphExists: (state) => {
      return doesGraphExist(state, state.ongoing.graphId)
    },

    isCurrentContextId: (state) => (id) => state.ongoing.contextId === id,

    currentContext: (state, getters) => {
      return getters.doesContextExist
        ? state.contexts[state.ongoing.contextId]
        : {}
    },

    currentGraph: (state, getters) => {
      return getters.doesGraphExists
        ? state.graphResults[state.ongoing.graphId]
        : {}
    },

    doesSearchExists: (state) => {
      return doesSearchExist(state, state.ongoing.searchId)
    },

    currentSearch: (state, getters) => {
      return getters.doesSearchExists
        ? state.searchResult[state.ongoing.searchId]
        : {}
    },

    sortList: (_, getters) => {
      return getters.currentContext.indexes || []
    },

    topFilters: (_, getters) => {
      return getters.currentContext.topFilters || {}
    },

    minPrice: (_, getters) => {
      if ('price' in getters.currentSearch) {
        return getters.currentSearch.price.min
      }

      return 0
    },

    maxPrice: (_, getters) => {
      if ('price' in getters.currentSearch) {
        return getters.currentSearch.price.max
      }

      return null
    },

    avgPrice: (_, getters) => {
      if ('price' in getters.currentSearch) {
        return getters.currentSearch.price.avg
      }

      return null
    },

    sortDefaultValue: (_, getters) => {
      const activeItem = getters.sortList.find((item) => item.active)

      return activeItem ? activeItem.value : ''
    },

    results: (_, getters) => {
      return getters.currentSearch.results || []
    },

    nbResults: (_, getters) => {
      const { nbResults } = getters.currentSearch

      return nbResults ? Number(nbResults) : 0
    },

    queryID: (_, getters) => {
      return getters.currentSearch.queryID || ''
    },

    hasResults: (_, getters) => {
      return !isEmpty(getters.results)
    },

    nbPages: (_, getters) => {
      return Number(getters.currentSearch.nbPages)
    },

    currentPage: (_, getters) => {
      const { currentPage } = getters.currentSearch

      return currentPage + 1
    },

    graphData: (_, getters) => {
      if ('price' in getters.currentSearch) {
        const { max } = getters.currentSearch.price
        const { facetsContext, facetsSearch } = getters.currentGraph

        if (!isEmpty(facetsContext) && !isEmpty(facetsSearch)) {
          const facetGraph = facetsContext.find(
            (facet) => facet.displayType === FACET_GRAPH,
          )

          if (!isEmpty(facetGraph)) {
            const correctScale = {}
            const orderedFacetsContext = [...facetGraph.scales].sort(
              (item1, item2) => item1.maxValue - item2.maxValue,
            )
            orderedFacetsContext.some((scale) => {
              if (scale.maxValue >= max) {
                correctScale.max = scale.maxValue
                correctScale.name = scale.facet

                return true
              }

              return false
            })

            const correctRange = facetsSearch.find(
              (facet) => facet.name === correctScale.name,
            )

            if (!isEmpty(correctRange)) {
              const dataRange = Array(RANGE_BAR).fill(0)
              Object.entries(correctRange.data).forEach(([key, value]) => {
                dataRange[key] = value
              })

              return {
                range: Math.floor(correctScale.max / RANGE_BAR),
                values: dataRange,
                max: correctScale.max,
              }
            }
          }
        }

        return {
          range: Math.floor(max / RANGE_BAR),
          values: [],
          max,
        }
      }

      return { range: 0, values: [], max: 0 }
    },

    values: (_, getters) => {
      return getters.graphData ? getters.graphData.values : []
    },

    range: (_, getters) => {
      return getters.graphData ? getters.graphData.range : 0
    },

    maxRange: (_, getters) => {
      return getters.graphData ? getters.graphData.max : 0
    },

    facets: (_, getters) => {
      const { facets: facetsContext } = getters.currentContext
      const { facets: facetsSearch } = getters.currentSearch

      if (!isEmpty(facetsContext) && !isEmpty(facetsSearch)) {
        return (
          facetsContext
            // Our API returns facet filters, but also numeric filters. Since numeric
            // filters should be treated differently from facet filters (we do not
            // display them the same way for exemple), let's filter them out.
            .filter((facet) => facet.facetType === FACET_FILTER)
            .reduce((acc, current) => {
              const { facetName, manualSort } = current

              const facetSearch =
                facetsSearch.find((f) => f.name === facetName) || {}

              const facetValues = facetSearch.data
                ? orderFacets(facetSearch.data, manualSort)
                : []

              if (isEmpty(facetValues)) {
                return acc
              }

              return [
                ...acc,
                {
                  ...current,
                  manualSort,
                  facet_name: facetName,
                  facet_values: facetValues,
                },
              ]
            }, [])
        )
      }

      return []
    },

    filterWhitelist: (_, getters) => {
      return getters.facets.map((facet) => facet.facet_name)
    },

    hasCarrierFacet: (_, getters) => {
      return getters.facets.some((facet) => facet.facet_name === FACET_CARRIER)
    },
    carriersFilter: (_, getters) => {
      return getters.currentContext?.topFilters?.carriers
    },
    simlockFilter: (_, getters) => {
      return getters.currentContext?.topFilters?.simlock
    },
  },
  mutations: {
    saveContextDetails(
      state,
      {
        searchParams,
        refinements,
        facets,
        indexes,
        numericFilters,
        topFilters,
      },
    ) {
      state.contexts = {
        ...state.contexts,
        [searchParams.contextId]: {
          // Sorting values
          indexes,
          // Facets we want to enable for the context (Ex: payment method, color)
          facets,
          // Type of product we want to search on (Ex: Apple, smartphone)
          refinements,
          numericFilters,
          topFilters,
        },
      }
    },

    saveSearch(
      state,
      {
        searchParams,
        hits,
        facets,
        nbHits,
        nbPages,
        page,
        stats = {},
        queryID,
      },
    ) {
      state.searchResult = {
        ...state.searchResult,
        [searchParams.searchId]: {
          results: hits,
          nbResults: nbHits,
          currentPage: page,
          nbPages,
          ttl: createTTL({ minutes: SEARCH_CACHE_DURATION_IN_MINUTES }),
          // Facets shown for this search
          facets,
          price: {
            min: stats.min || 0,
            max: stats.max || 0,
            avg: stats.avg || 0,
          },
          queryID,
        },
      }
    },

    saveGraph(state, { searchParams, facetsSearch, facetsContext }) {
      state.graphResults = {
        ...state.graphResults,
        [searchParams.graphId]: {
          facetsSearch,
          facetsContext,
        },
      }
    },

    registerCurrentSearch(state, searchParams) {
      state.ongoing = {
        contextId: searchParams.contextId,
        searchId: searchParams.searchId,
        graphId: searchParams.graphId,
      }
    },
  },
  actions: {
    async fetchResults({ commit, state, dispatch }, { searchParams }) {
      try {
        if (!(searchParams.contextId in state.contexts)) {
          const { payload } = await dispatch(
            'http/request',
            {
              request: algoliaRefinementsFetch,
              queryParams: removeEmptyValuesInObject({
                source: searchParams.context.source,
                // TODO: Remove when NAV-1502 will be done
                indexType: 'product',
                slug: searchParams.context.slug,
                id: searchParams.context.id,
                searchbar_type: searchParams.context.searchBarType,
              }),
            },
            { root: true },
          )

          commit('saveContextDetails', {
            ...payload,
            searchParams,
          })
        }

        if (!doesSearchExist(state, searchParams.searchId)) {
          await dispatch('search', { searchParams })
        }

        commit('registerCurrentSearch', searchParams)
      } catch {
        // do nothing
      }
    },

    async search({ state, commit, rootGetters }, { searchParams }) {
      // The context is not registered yet as ongoing, since we don't want to refresh the client
      // as long as we don't have the search call received
      const context = state.contexts[searchParams.contextId]
      const endpointName = 'algoliaSearch'
      const logMessage = `GET ${endpointName}`

      const apiLogger = new APILogger({
        endpointName,
        platform: PLATFORM,
        bundleId: BUNDLE_ID,
        context,
        visitor_id: rootGetters['config/visitorId'],
        filters: searchParams.toFilters(),
      })

      apiLogger.attempt({ message: logMessage })

      try {
        const searchResults = await useAlgolia({
          ALGOLIA_ID: this.app.$config.ALGOLIA_ID,
          ALGOLIA_KEY: this.app.$config.ALGOLIA_KEY,
          context,
          searchParams,
          attributesToRetrieve: rootGetters['landing/attributesToRetrieve'],
        })

        apiLogger.success({
          message: logMessage,
          index: searchResults.index,
        })

        commit('saveSearch', searchResults)
        if (!doesGraphExist(state, searchParams.graphId)) {
          const { facets: facetsSearch } = searchResults
          const { facets: facetsContext } = context

          commit('saveGraph', { searchParams, facetsSearch, facetsContext })
        }
      } catch (error) {
        apiLogger.fail({
          message: logMessage,
          error,
        })
        throw error
      }
    },
  },
}
